import { useState } from 'react';
import { Link } from 'react-router-dom';
import { JsonImport } from '@/components/import/JsonImport';
import { importApi } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';

export function ImportPage() {
  const [isClearing, setIsClearing] = useState(false);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculateResult, setRecalculateResult] = useState<{
    success: boolean;
    updated?: number;
    failed?: number;
    total?: number;
    error?: string;
  } | null>(null);
  const [isFixingDates, setIsFixingDates] = useState(false);
  const [fixDatesResult, setFixDatesResult] = useState<{
    success: boolean;
    positionsFixed?: number;
    chainsFixed?: number;
    error?: string;
  } | null>(null);
  const [isClosingExpired, setIsClosingExpired] = useState(false);
  const [closeExpiredResult, setCloseExpiredResult] = useState<{
    success: boolean;
    positionsClosed?: number;
    error?: string;
  } | null>(null);
  const [isFixingTransactions, setIsFixingTransactions] = useState(false);
  const [fixTransactionsResult, setFixTransactionsResult] = useState<{
    success: boolean;
    transactionsCreated?: number;
    chainsUpdated?: number;
    error?: string;
  } | null>(null);
  const [clearCounts, setClearCounts] = useState<{
    chains: number;
    positions: number;
    legs: number;
    transactions: number;
  } | null>(null);
  const [clearResult, setClearResult] = useState<{
    success: boolean;
    deleted?: { chains: number; positions: number; legs: number; transactions: number };
    error?: string;
  } | null>(null);
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const handleShowClearConfirm = async () => {
    setIsLoadingCounts(true);
    setClearResult(null);

    try {
      const data = await importApi.clearDataPreview();
      setClearCounts(data.counts);
      // Only show confirm if there's actually data to delete
      const hasData = data.counts.chains > 0 || data.counts.positions > 0 ||
                      data.counts.legs > 0 || data.counts.transactions > 0;
      setShowConfirmClear(hasData);
    } catch (error) {
      setClearResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get data counts',
      });
    } finally {
      setIsLoadingCounts(false);
    }
  };

  // Check if there's no data to clear
  const hasNoData = clearCounts &&
    clearCounts.chains === 0 &&
    clearCounts.positions === 0 &&
    clearCounts.legs === 0 &&
    clearCounts.transactions === 0;

  const handleClearData = async () => {
    setIsClearing(true);
    setClearResult(null);

    try {
      const data = await importApi.clearData();

      setClearResult({
        success: true,
        deleted: data.deleted,
      });
      setClearCounts(null);
    } catch (error) {
      setClearResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to clear data',
      });
    } finally {
      setIsClearing(false);
      setShowConfirmClear(false);
    }
  };

  const handleCancelClear = () => {
    setShowConfirmClear(false);
    setClearCounts(null);
  };

  const handleRecalculatePnl = async () => {
    setIsRecalculating(true);
    setRecalculateResult(null);

    try {
      const data = await importApi.recalculatePnl();
      setRecalculateResult({
        success: true,
        updated: data.updated,
        failed: data.failed,
        total: data.total,
      });
    } catch (error) {
      setRecalculateResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to recalculate P&L',
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleFixHistoricalDates = async () => {
    setIsFixingDates(true);
    setFixDatesResult(null);

    try {
      const data = await importApi.fixHistoricalDates();
      setFixDatesResult({
        success: true,
        positionsFixed: data.positionsFixed,
        chainsFixed: data.chainsFixed,
      });
    } catch (error) {
      setFixDatesResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fix historical dates',
      });
    } finally {
      setIsFixingDates(false);
    }
  };

  const handleCloseExpired = async () => {
    setIsClosingExpired(true);
    setCloseExpiredResult(null);

    try {
      const data = await importApi.closeExpired();
      setCloseExpiredResult({
        success: true,
        positionsClosed: data.positionsClosed,
      });
    } catch (error) {
      setCloseExpiredResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to close expired positions',
      });
    } finally {
      setIsClosingExpired(false);
    }
  };

  const handleFixMissingTransactions = async () => {
    setIsFixingTransactions(true);
    setFixTransactionsResult(null);

    try {
      const data = await importApi.fixMissingCloseTransactions();
      setFixTransactionsResult({
        success: true,
        transactionsCreated: data.transactionsCreated,
        chainsUpdated: data.chainsUpdated,
      });
    } catch (error) {
      setFixTransactionsResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fix missing transactions',
      });
    } finally {
      setIsFixingTransactions(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/settings"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Import Trades</h1>
        <p className="text-sm text-gray-500 mt-1">
          Import your trades from Schwab
        </p>
      </div>

      <JsonImport />

      {/* Close Expired Positions Section */}
      <Card className="border-purple-200 bg-purple-50">
        <CardHeader>
          <CardTitle className="text-purple-800">Close Expired Positions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-purple-700 mb-4">
            After importing, use this to close any positions whose options have expired.
            This marks expired options as closed with $0 value.
          </p>

          {closeExpiredResult && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              closeExpiredResult.success
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {closeExpiredResult.success ? (
                <>
                  Closed {closeExpiredResult.positionsClosed} expired position{closeExpiredResult.positionsClosed !== 1 ? 's' : ''}
                </>
              ) : (
                <>Error: {closeExpiredResult.error}</>
              )}
            </div>
          )}

          <Button
            variant="secondary"
            onClick={handleCloseExpired}
            disabled={isClosingExpired}
          >
            {isClosingExpired ? 'Closing...' : 'Close Expired Positions'}
          </Button>
        </CardContent>
      </Card>

      {/* Fix Missing Close Transactions Section */}
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader>
          <CardTitle className="text-orange-800">Fix Missing Close Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-orange-700 mb-4">
            Fixes legs marked as closed but missing closing transactions.
            This corrects P&L calculations for positions that expired or were closed incorrectly.
          </p>

          {fixTransactionsResult && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              fixTransactionsResult.success
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {fixTransactionsResult.success ? (
                <>
                  Created {fixTransactionsResult.transactionsCreated} transaction{fixTransactionsResult.transactionsCreated !== 1 ? 's' : ''},
                  updated {fixTransactionsResult.chainsUpdated} chain{fixTransactionsResult.chainsUpdated !== 1 ? 's' : ''}
                </>
              ) : (
                <>Error: {fixTransactionsResult.error}</>
              )}
            </div>
          )}

          <Button
            variant="secondary"
            onClick={handleFixMissingTransactions}
            disabled={isFixingTransactions}
          >
            {isFixingTransactions ? 'Fixing...' : 'Fix Missing Transactions'}
          </Button>
        </CardContent>
      </Card>

      {/* Recalculate P&L Section */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-blue-800">Recalculate P&L</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-700 mb-4">
            After importing, use this to recalculate profit/loss for all trade chains.
            This updates premium totals and realized P&L values.
          </p>

          {recalculateResult && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              recalculateResult.success
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {recalculateResult.success ? (
                <>
                  Updated {recalculateResult.updated} of {recalculateResult.total} chains
                  {recalculateResult.failed !== undefined && recalculateResult.failed > 0 && (
                    <> ({recalculateResult.failed} failed)</>
                  )}
                </>
              ) : (
                <>Error: {recalculateResult.error}</>
              )}
            </div>
          )}

          <Button
            variant="secondary"
            onClick={handleRecalculatePnl}
            disabled={isRecalculating}
          >
            {isRecalculating ? 'Recalculating...' : 'Recalculate All P&L'}
          </Button>
        </CardContent>
      </Card>

      {/* Fix Historical Dates Section */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="text-amber-800">Fix Historical Dates</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-amber-700 mb-4">
            If your P&L history shows all data on today's date, use this to repair the historical
            close dates from transaction data. Run this after importing if the P&L chart is incorrect.
          </p>

          {fixDatesResult && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              fixDatesResult.success
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {fixDatesResult.success ? (
                <>
                  Fixed {fixDatesResult.positionsFixed} positions and {fixDatesResult.chainsFixed} chains
                </>
              ) : (
                <>Error: {fixDatesResult.error}</>
              )}
            </div>
          )}

          <Button
            variant="secondary"
            onClick={handleFixHistoricalDates}
            disabled={isFixingDates}
          >
            {isFixingDates ? 'Fixing Dates...' : 'Fix Historical Dates'}
          </Button>
        </CardContent>
      </Card>

      {/* Clear Data Section - hide after successful clear */}
      {clearResult?.success ? (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-4">
            <div className="text-sm text-green-700">
              Data cleared successfully:
              <ul className="list-disc ml-5 mt-1">
                <li>{clearResult.deleted?.chains ?? 0} trade chains</li>
                <li>{clearResult.deleted?.positions ?? 0} positions</li>
                <li>{clearResult.deleted?.legs ?? 0} legs</li>
                <li>{clearResult.deleted?.transactions ?? 0} transactions</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      ) : (
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-800">Clear All Trade Data</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-700 mb-4">
            This will permanently delete all your trade chains, positions, legs, and transactions.
            Use this to start fresh before re-importing. This action cannot be undone.
          </p>

          {clearResult && !clearResult.success && (
            <div className="mb-4 p-3 rounded-lg text-sm bg-red-100 text-red-700">
              Error: {clearResult.error}
            </div>
          )}

          {hasNoData ? (
            <div className="text-sm text-gray-600">
              No trade data to clear.
            </div>
          ) : showConfirmClear && clearCounts ? (
            <div className="space-y-3">
              <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-sm text-red-800">
                <p className="font-medium mb-2">This will permanently delete:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><span className="font-semibold">{clearCounts.chains}</span> trade chains</li>
                  <li><span className="font-semibold">{clearCounts.positions}</span> positions</li>
                  <li><span className="font-semibold">{clearCounts.legs}</span> legs</li>
                  <li><span className="font-semibold">{clearCounts.transactions}</span> transactions</li>
                </ul>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleClearData}
                  disabled={isClearing}
                >
                  {isClearing ? 'Clearing...' : 'Yes, Delete Everything'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancelClear}
                  disabled={isClearing}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="danger"
              onClick={handleShowClearConfirm}
              disabled={isLoadingCounts}
            >
              {isLoadingCounts ? 'Loading...' : 'Clear All Data'}
            </Button>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
